import { CommonModule } from '@angular/common';
import {
	Component,
	QueryList,
	ViewChildren,
	ViewChild,
	Input,
	computed,
	inject,
	effect,
	signal,
	AfterViewInit,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import { ISessionEntry } from '../timing-tower/IRaceTiming';
import { VbLxpCheckboxComponent } from '../controls/checkbox/vb-lxp-checkbox.component';
import { TimespanPipe } from '../shared/time-span.pipe';
import { TimingTowerComponent } from '../timing-tower/timing-tower.component';
import { VbLxpPanelComponent } from '../panel/vb-lxp-panel.component';
import { VbLxpVideoSlotComponent } from '../video/video-slot/video-slot.component';
import { WindowSizeService } from '../shared/window-size.service';
import { VideoLayout, VideoLayoutService } from '../shared/video-layout.service';
import { FeatureSettingsService } from '../shared/feature-settings.service';
import { PanelService } from '../panel/panel.service';
import { StreamData } from '../shared/stream.token';
import { RaceSessionService } from '../timing-tower/race-session.service';
import { SwissTimingIframeComponent } from '../timing-tower/swiss-timing-iframe/swiss-timing-iframe.component';
import { RaceClassComponent } from '../shared/race-class.component';
import { ENABLE_TWITCH_STREAM_SELECTOR } from '../shared/livestream-options.token';
import { RaceInfoService } from '../shared/race-info.service';

import { LivestreamViewService } from './livestream-view.service';
import { TwitchChatComponent } from '../twitch-chat/twitch-chat.component';
import { FavoriteDriverService } from '../shared/favorite-driver.service';
import { TimingTowerService } from '../timing-tower/timing-tower.service';
import { VisibilitiesService } from '../shared/visibilities.service';
import { WebSocketService } from '../shared/websocket.service';
import { SWISS_TIMING_ENABLED } from '../lxp/lxp.tokens';

const LAYOUT_CLASS_MAP: Record<VideoLayout, string> = {
	[VideoLayout.SINGLE]: 'aspect-16/9',
	[VideoLayout.SIDE_BY_SIDE]: 'aspect-32/9',
	[VideoLayout.LARGE_LEFT]: 'aspect-48/18',
	[VideoLayout.LARGE_RIGHT]: 'aspect-48/18',
	[VideoLayout.GRID_2X2]: 'aspect-16/9',
	[VideoLayout.STACKED]: 'aspect-16/18',
};

@Component({
	selector: 'app-livestream-view',
	standalone: true,
	imports: [
		CommonModule,
		TimespanPipe,
		RouterModule,
		VbLxpCheckboxComponent,
		VbLxpPanelComponent,
		VbLxpVideoSlotComponent,
		TwitchChatComponent,
		TimingTowerComponent,
		RaceClassComponent,
		SwissTimingIframeComponent,
	],
	templateUrl: './livestream-view.component.html',
	styleUrl: './livestream-view.component.css',
	host: {
		'[style.height]': `'100%'`,
	},
})
export class LivestreamViewComponent implements AfterViewInit {
	@Input() public streamChatTitle = 'SRO Stream Chat';
	@Input() public enableStreamChatPopout = true;
	@Input() public streamChatPopoutUrl =
		`https://www.twitch.tv/popout/sromotorsports/chat?popout=&parent=${window.location.hostname}`;

	@ViewChildren('livestreamPlayerSlot')
	currentLivestreamPlayerSlots: QueryList<VbLxpVideoSlotComponent>;

	@ViewChild('timingTower')
	timingTower: TimingTowerComponent;

	public layoutService = inject(VideoLayoutService);
	public raceSessionService = inject(RaceSessionService);
	public raceInfoService = inject(RaceInfoService);

	private windowSizeService = inject(WindowSizeService);
	private panelService = inject(PanelService);
	private featureSettingsService = inject(FeatureSettingsService);
	public readonly visibilitiesService = inject(VisibilitiesService);
	public showTwitchStreamSelector =
		inject(ENABLE_TWITCH_STREAM_SELECTOR, { optional: true }) ?? false;
	public livestreamViewService = inject(LivestreamViewService);
	public favoriteDriverService = inject(FavoriteDriverService);
	public timingTowerService = inject(TimingTowerService);
	private readonly webSocketService = inject(WebSocketService);
	public readonly isSwissTimingEnabled = inject(SWISS_TIMING_ENABLED, { optional: true }) ?? false;

	public VideoLayout = VideoLayout;
	public isMobile = toSignal(this.windowSizeService.isMobile$);
	public moreColumns = signal(false);
	public isMainStreamSelected = signal(true);
	public isSessionArchived = signal(false);
	public prevIsSessionArchived = signal(false);
	public featureSettings = toSignal(this.featureSettingsService.featureSettings$);
	public selectedClasses = toSignal(this.raceSessionService.selectedClasses$, { initialValue: [] });

	// Delegate to RaceInfoService
	public raceStatus = this.raceInfoService.raceStatus;
	public timeStatus = this.raceInfoService.timeStatus;
	public trackTempDisplay = this.raceInfoService.trackTempDisplay;
	public windSpeedDisplay = this.raceInfoService.windSpeedDisplay;
	public windDirectionDegrees = this.raceInfoService.windDirectionDegrees;
	public windDirectionLabel = this.raceInfoService.windDirectionLabel;
	public localTimeDisplay = this.raceInfoService.localTimeDisplay;

	public hidePlayerMobile = computed(() => {
		return this.isMobile() && this.livestreamViewService.isNoPlayerSelected();
	});

	protected containerAspectRatioClass = '';

	constructor() {
		this.layoutService.activeLayout$.subscribe((activeLayout: VideoLayout) => {
			this.containerAspectRatioClass = LAYOUT_CLASS_MAP[activeLayout];
		});

		this.raceSessionService.isSessionArchived$
			.pipe(takeUntilDestroyed())
			.subscribe((isArchived) => {
				this.prevIsSessionArchived.set(this.isSessionArchived());
				this.isSessionArchived.set(isArchived);
				if (isArchived && !this.layoutService.isLayout(VideoLayout.SINGLE)) {
					this.layoutService.setLayout(VideoLayout.SINGLE);
				}
				if (isArchived && this.panelService.isPanelOpen('timing')) {
					this.panelService.closePanel('timing');
				}
				// Open timing tower when session switches from archived to not archived
				if (this.prevIsSessionArchived() && !this.isSessionArchived()) {
					this.panelService.openPanel('timing');
				}
			});

		effect(() => {
			if (this.moreColumns()) {
				this.panelService.closePanel('chat');
			}
		});

		effect(() => {
			const chatOpen = this.chatPanelOpen();
			if (chatOpen && this.moreColumns()) {
				this.panelService.closePanel('timing');
				setTimeout(() => this.panelService.openPanel('chat'));
			}
		});

		effect(() => {
			const timingOpen = this.timingPanelOpen();
			if (timingOpen && this.moreColumns()) {
				this.panelService.closePanel('chat');
				setTimeout(() => this.panelService.openPanel('timing'));
			}
		});
	}

	private chatPanelOpen = toSignal(this.panelService.getPanelState('chat'));
	private timingPanelOpen = toSignal(this.panelService.getPanelState('timing'));

	ngAfterViewInit(): void {
		// SSE connection is now managed centrally in app.ts

		const currentPlayers = this.currentLivestreamPlayerSlots.toArray().map((lps) => lps.mvPlayer);
		this.livestreamViewService.updateCurrentPlayers(currentPlayers);
		if (this.isSessionArchived() && this.panelService.isPanelOpen('timing')) {
			this.panelService.closePanel('timing');
		}
		this.setFavoriteDrivers();
	}

	public openFullTimingPage() {
		const currentQueryParams = window.location.search;
		const newRoute = '/full-timing';
		const fullUrl = `${newRoute}${currentQueryParams}`;

		window.open(fullUrl, '_blank');
	}

	public openColumnSettings() {
		this.timingTower?.openColumnSettings();
	}

	public openFullMapPage() {
		const currentParams = new URLSearchParams(window.location.search).toString();
		const base = new URL('map-popout', document.baseURI).toString();
		const url = currentParams ? `${base}?${currentParams}` : base;
		const width = 1280;
		const height = 720;
		window.open(url, '_blank', `width=${width},height=${height},resizable=yes`);
	}

	public openStreamChatPopout() {
		if (!this.streamChatPopoutUrl) {
			return;
		}

		const width = 420;
		const height = 760;
		const left = Math.max((window.screen.width - width) / 2, 0);
		const top = Math.max((window.screen.height - height) / 2, 0);

		window.open(
			this.streamChatPopoutUrl,
			'stream-chat-popout',
			`popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
		);
	}

	public setFavoriteDrivers() {
		const favoriteDrivers = this.raceSessionService.raceTeamsPosition().filter((t) => {
			return (
				this.favoriteDriverService.isFavoriteDriver(t) &&
				this.livestreamViewService.getStreamDataByCarNumber(t.number).isLive
			);
		});
		if (favoriteDrivers.length === 2) {
			this.layoutService.setLayout(VideoLayout.STACKED);
		} else if (favoriteDrivers.length === 3) {
			this.layoutService.setLayout(VideoLayout.LARGE_LEFT);
		} else if (favoriteDrivers.length >= 4) {
			this.layoutService.setLayout(VideoLayout.GRID_2X2);
		}
		favoriteDrivers.forEach((d) => {
			const streamData = this.livestreamViewService.getStreamDataByCarNumber(d.number);
			if (!streamData.isLive) return;

			this.timingTowerService.setActive(d.number);
			this.livestreamViewService.addSlotItem({
				type: 'timing',
				id: d.number,
				streamData,
				timestamp: Date.now(),
			});
		});
	}

	public onTimingTowerSelectionAdded(team: ISessionEntry) {
		this.livestreamViewService.addSlotItem({
			type: 'timing',
			id: team.number,
			streamData: this.livestreamViewService.getStreamDataByCarNumber(team.number),
			timestamp: Date.now(),
		});
	}

	public onTimingTowerSelectionRemoved(team: ISessionEntry) {
		this.livestreamViewService.removeSlotItem({
			type: 'timing',
			id: team.number,
			streamData: this.livestreamViewService.getStreamDataByCarNumber(team.number),
			timestamp: Date.now(),
		});
	}

	public onTwitchStreamToggled(isSelected: boolean) {
		this.livestreamViewService.onTwitchStreamToggled(isSelected);
	}

	public toggleAdvanced() {
		this.moreColumns.update((v) => !v);
	}

	public onPlayerClosed(streamData: StreamData) {
		if (streamData.id?.includes('main-stream') && !streamData.id.includes('twitch')) {
			this.livestreamViewService.removeSlotItem({
				type: 'mainStream',
			});
		} else if (streamData.id?.includes('twitch')) {
			this.livestreamViewService.removeSlotItem({
				type: 'twitchStream',
			});
		} else if (streamData.carNumber) {
			this.livestreamViewService.removeSlotItem({
				type: 'timing',
				id: streamData.carNumber,
				streamData,
				timestamp: Date.now(),
			});
		}
	}

	onClassSelectionChange(selectedClasses: string[]) {
		this.raceSessionService.updateSelectedClasses(selectedClasses);
	}
}
